import { css } from '@emotion/react'
import { useState } from 'react'

import { Icon } from '@/component/atoms/Icon'
import { localStorageService } from '@/service/storage.service'

import QrScanLayer from './QrScanLayer'
import { qrScanStore } from './qrScanStore'

// 처음 보는 선생님에게 자리를 알리는 용도라, 한 번 눌러 본 브라우저에서는 다시 돌지 않게 남긴다
const readSeen = () => {
  try {
    return localStorageService.getItem('QR_SCAN_BUTTON_SEEN') === true
  } catch {
    return false
  }
}

const writeSeen = () => {
  try {
    localStorageService.setItem('QR_SCAN_BUTTON_SEEN')(true)
  } catch {
    // 저장이 막혀도 이번 로드 동안은 state 로 멈춰 있다
  }
}

const QrScanButton = () => {
  const [isSeen, setIsSeen] = useState(readSeen)

  if (!qrScanStore.canStart) return null

  const onClick = () => {
    if (!isSeen) {
      setIsSeen(true)
      writeSeen()
    }
    qrScanStore.start()
  }

  return (
    <>
      <button
        type="button"
        className="flex handler-button"
        aria-label="QR 채점"
        title="QR 채점"
        onClick={onClick}
      >
        <div className="icon__wrapper">
          <span css={S.glyphBox}>
            {isSeen ? (
              <Icon name="qr_scan" size={16} color="currentColor" />
            ) : (
              <>
                <span css={S.glyphCycle}>
                  <Icon name="qr_scan" size={16} color="currentColor" />
                </span>
                <span css={S.scanClip} aria-hidden>
                  <span css={S.scanGlow} />
                </span>
                <span css={S.wordCycle} aria-hidden>
                  채점
                </span>
              </>
            )}
          </span>
        </div>
      </button>
      <QrScanLayer />
    </>
  )
}

export default QrScanButton

/*
 * 누르기 전에는 10초 한 바퀴 — 0~6% 아이콘이 Sky 500 으로 물들고, 8~28% 빛 띠가 위에서 아래로 쓸고,
 * 32~40% 제자리에서 `채점` 으로 바뀌어 60% 까지 머물고, 68% 에 아이콘으로 돌아와 쉰다.
 * emotion keyframes 보간이 이 앱 빌드에서 스타일을 통째로 잃는 경우가 있어 이름 있는 @keyframes 를 직접 둔다
 */
const GNB_CYCLE = '10s ease-in-out infinite'

const S = {
  glyphBox: css`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    color: var(--Colors-Gray-800);
  `,
  glyphCycle: css`
    @keyframes mf-qr-gnb-glyph {
      0% {
        opacity: 1;
        transform: scale(1);
        color: var(--Colors-Gray-800);
        filter: blur(0);
      }
      6%,
      32% {
        opacity: 1;
        transform: scale(1);
        color: var(--Colors-Sky-500);
        filter: blur(0);
      }
      40%,
      60% {
        opacity: 0;
        transform: scale(0.85);
        color: var(--Colors-Sky-500);
        filter: blur(1px);
      }
      68%,
      100% {
        opacity: 1;
        transform: scale(1);
        color: var(--Colors-Gray-800);
        filter: blur(0);
      }
    }
    display: inline-flex;
    animation: mf-qr-gnb-glyph ${GNB_CYCLE};

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `,
  scanClip: css`
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
  `,
  scanGlow: css`
    @keyframes mf-qr-gnb-sweep {
      0%,
      8% {
        transform: translateY(-10px);
      }
      28%,
      100% {
        transform: translateY(20px);
      }
    }
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background: linear-gradient(
      to bottom,
      transparent,
      color-mix(in srgb, var(--Colors-Sky-300) 55%, transparent)
    );
    transform: translateY(-10px);
    animation: mf-qr-gnb-sweep ${GNB_CYCLE};

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `,
  wordCycle: css`
    @keyframes mf-qr-gnb-word {
      0%,
      32% {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.85);
        filter: blur(1px);
      }
      40%,
      60% {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
        filter: blur(0);
      }
      68%,
      100% {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.85);
        filter: blur(1px);
      }
    }
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 12px;
    line-height: 18px;
    font-weight: 700;
    color: var(--Colors-Sky-500);
    white-space: nowrap;
    opacity: 0;
    animation: mf-qr-gnb-word ${GNB_CYCLE};

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `,
}
